import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, Pencil, Plus, Copy, ExternalLink,
} from 'lucide-react';
import { fetchJSON } from '../utils/api';
import { formatDate } from '../utils/dateFormat';
import { DateChip, fmtHours, todayISO } from '../components/lnd/SessionRow';
import { trainerRoleLabel } from '../components/lnd/trainerUtils';
import SessionMaterials from '../components/lnd/SessionMaterials';
import RefLink from '../components/shared/RefLink';
import { btnPrimary } from '../components/shared/FormKit';
import { sectionTitle, sectionTitleBar } from '../components/workflow/constants';

/**
 * One training session, as the people on it see it — /my/sessions/:id
 * (docs/modules/my-records.md §6).
 *
 * Everyone on the session gets the same page: the facts, the course
 * materials and the quiz. What differs is the strip of actions, and the
 * SERVER decides it (`me` in the response):
 *   - a participant sees Take / Retake Quiz when they are in the audience,
 *     and Give Feedback once the session has ended if they attended
 *   - a trainer sees Trainer Tools: upload / remove materials, Create /
 *     Edit / Copy the quiz — until L&D marks the session completed; the
 *     recording (a video, or a link to one) stays open to them after that,
 *     because it is made after the session
 *   - L&D sees the materials controls too, and a door into Training Records
 *
 * Not being on the session is a normal outcome (a bookmarked link, a
 * forwarded mail), so it gets a plain explanation, not an access wall.
 */

const ROLE_LABEL = {
  trainer: 'You are a trainer of this session',
  attended: 'You attended this session',
  absent: 'You were absent from this session',
  expected: 'You are on the attendee list',
  ld: 'Viewing as L&D',
};

const Card = ({ title, right, children }) => (
  <section className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
    <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-100">
      <h3 className={`${sectionTitle} flex-1`}>
        <span className={sectionTitleBar} aria-hidden="true" />
        {title}
      </h3>
      {right}
    </div>
    {children}
  </section>
);

// A line of session facts, separated by middots; falsy items are skipped.
// One labelled fact in the header grid; `empty` is what to print when the
// value is missing (a venue not set is information, not a gap in the layout).
const Fact = ({ label, value, empty = '—' }) => (
  <div className="min-w-0">
    <dt className="text-[11px] uppercase tracking-wide text-slate-500">{label}</dt>
    <dd className={`mt-0.5 text-sm break-words ${value ? 'text-slate-800' : 'text-slate-400'}`}>{value || empty}</dd>
  </div>
);

// Why the trainer cannot change the paper from here, in a sentence.
const EDIT_BLOCK_TEXT = {
  completed: 'This session is marked completed — the quiz is frozen. L&D can still change it from Training Records.',
  shared: 'This quiz is shared with other sessions, so it cannot be changed from here. Make a copy for this session to edit it, or ask L&D to edit it in the Quiz Bank.',
  locked: 'Someone has already sat this quiz — its questions are frozen so recorded scores keep their meaning.',
};

const MySessionDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [denied, setDenied] = useState(false);
  const [copying, setCopying] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await fetchJSON(`/api/my/sessions/${id}`);
      setData(d);
      setError(null);
    } catch (e) {
      if (e.status === 403) setDenied(true);
      else setError(e.message);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const copyQuiz = async () => {
    setCopying(true);
    setNotice(null);
    try {
      await fetchJSON(`/api/ld/sessions/${id}/quiz/copy`, { method: 'POST' });
      setNotice('The quiz is now this session’s own copy — you can edit it.');
      await load();
    } catch (e) { setError(e.message); }
    finally { setCopying(false); }
  };

  const back = (
    <Link to="/my?tab=sessions" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
      <ArrowLeft className="w-4 h-4" /> My Records
    </Link>
  );

  if (denied) {
    return (
      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        {back}
        <div className="mt-4 bg-white rounded-xl border border-slate-200 shadow-sm px-5 py-10 text-center">
          <p className="text-sm text-slate-600">You are not on this training session.</p>
          <p className="text-xs text-slate-500 mt-1">
            A session page opens for its attendee list, its attendees, its trainers and L&D.
          </p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        {back}
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          {error}
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="p-4 sm:p-6 max-w-3xl mx-auto">{back}<p className="mt-4 text-sm text-slate-500">Loading…</p></div>;
  }

  const { session, me, files, quiz } = data;
  const recordings = data.recordings || [];
  const today = todayISO();
  const isTrainer = me.role === 'trainer';
  const trainerTools = isTrainer && !session.completed;
  const sitting = quiz?.sitting;
  const feedback = data.feedback;

  // The attendee's feedback button: Give Feedback, or a Submitted label.
  let feedbackCta = null;
  if (feedback) {
    if (feedback.submitted) {
      feedbackCta = <span className="text-sm font-medium text-emerald-700">Submitted</span>;
    } else if (!feedback.open) {
      feedbackCta = <span className="text-xs text-slate-500">Opens after the session ends</span>;
    } else {
      feedbackCta = <Link to={`/feedback/${session.id}`} className={`${btnPrimary} inline-flex items-center gap-1.5`}>Give Feedback</Link>;
    }
  }

  // The participant's quiz button: Take / Resume / Retake, or a Completed label.
  let quizCta = null;
  if (sitting?.inAudience && !isTrainer) {
    if (sitting.done) {
      quizCta = (
        <span className="text-sm font-medium text-emerald-700">Completed</span>
      );
    } else if (!sitting.open) {
      quizCta = <span className="text-xs text-slate-500">Opens on {formatDate(session.date)}</span>;
    } else {
      const label = sitting.inProgress ? 'Resume Quiz' : sitting.canRetake ? 'Retake Quiz' : 'Take Quiz';
      quizCta = <Link to={`/quiz/${session.id}`} className={`${btnPrimary} inline-flex items-center gap-1.5`}>{label}</Link>;
    }
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-5">
      {back}

      {/* Header: title + badges, then the facts as a labelled grid — one
          thing per cell, so nothing has to be read as a dot-joined sentence. */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-start gap-4">
          <DateChip date={session.date} today={today} tone={isTrainer ? 'emerald' : 'violet'} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-bold text-slate-900">{session.courseTitle}</h1>
              {isTrainer && (
                <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 text-xs font-semibold">
                  {trainerRoleLabel(me.trainerRole)}
                </span>
              )}
              {session.isMandatory && (
                <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-semibold">Mandatory</span>
              )}
              {session.completed && (
                <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-xs font-semibold">
                  Completed{session.completedDate ? ` ${formatDate(session.completedDate)}` : ''}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 mt-0.5">{ROLE_LABEL[me.role] || ''}</p>
          </div>
          {me.canOpenRecords && (
            <RefLink
              to="/ld/records?tab=sessions"
              className="shrink-0 inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 whitespace-nowrap"
            >
              <ExternalLink className="w-4 h-4" /> Training Records
            </RefLink>
          )}
        </div>

        <dl className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3">
          <Fact label="Date" value={formatDate(session.date)} />
          <Fact label="Time" value={session.startEndTime} empty="Not set" />
          <Fact label="Venue" value={session.venueName} empty="Not specified" />
          <Fact label="Hours" value={session.hours != null ? `${fmtHours(session.hours)} h` : null} />
          <Fact label="Category" value={session.categoryTitle} empty="Not classified" />
          <Fact label="Session ID" value={session.sessionCode} />
          <div className="col-span-2">
            <dt className="text-[11px] uppercase tracking-wide text-slate-500">Trainers</dt>
            <dd className="mt-0.5 text-sm">
              {(session.trainers || []).length === 0 ? (
                <span className="text-slate-400">Not assigned</span>
              ) : (
                <ul className="space-y-0.5">
                  {session.trainers.map((t) => (
                    <li key={t.staffId} className="text-slate-800">
                      {t.name} <span className="text-slate-400">— {trainerRoleLabel(t.role)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </dd>
          </div>
        </dl>
      </div>

      {isTrainer && session.completed && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          This session is marked completed. Materials and the quiz are read-only for trainers now; L&D can still make changes from Training Records. The recording can still be added below.
        </div>
      )}

      {notice && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
          {notice}
        </div>
      )}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* L&D reads here, manages in Training Records — one door each. */}
      {me.role === 'ld' && me.canOpenRecords && (
        <p className="text-xs text-slate-500">
          Materials and the recording are managed from Training Records → Sessions → the folder action; this page shows what the participants see.
        </p>
      )}

      {/* Course materials */}
      <Card title="Course Materials" right={<span className="text-xs text-slate-500">{files.length} file{files.length === 1 ? '' : 's'}</span>}>
        <SessionMaterials
          sessionId={session.id}
          files={files}
          canManage={me.canManageFiles}
          onChanged={(next) => setData((d) => ({ ...d, files: next, session: { ...d.session, fileCount: next.length } }))}
        />
      </Card>

      {/* The recording — a video uploaded after the session, or a link to it. */}
      <Card title="Recording" right={<span className="text-xs text-slate-500">{recordings.length} item{recordings.length === 1 ? '' : 's'}</span>}>
        <SessionMaterials
          sessionId={session.id}
          kind="recording"
          files={recordings}
          canManage={me.canManageRecordings}
          onChanged={(next) => setData((d) => ({ ...d, recordings: next, session: { ...d.session, recordingCount: next.length } }))}
        />
      </Card>

      {/* The quiz */}
      <Card title="Post-Course Quiz" right={quizCta}>
        {quiz ? (
          <div className="px-5 py-4 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900">{quiz.title}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {quiz.questionCount} question{quiz.questionCount === 1 ? '' : 's'}
                  {' · '}{quiz.passMark != null ? `pass mark ${quiz.passMark}%` : 'score only'}
                  {quiz.mustPassCount > 0 ? ` · ${quiz.mustPassCount} must-answer question${quiz.mustPassCount === 1 ? '' : 's'}` : ''}
                  {' · '}everyone expected sits it
                </p>
                {quiz.description && <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{quiz.description}</p>}
              </div>
              {me.canOpenQuizEditor && (
                <div className="flex flex-wrap gap-2 shrink-0">
                  {quiz.editable && (
                    <Link to={`/my/sessions/${session.id}/quiz`} className={`${btnPrimary} inline-flex items-center gap-1.5`}>
                      <Pencil className="w-4 h-4" /> Edit Quiz
                    </Link>
                  )}
                  {!quiz.editable && (
                    <Link to={`/my/sessions/${session.id}/quiz`} className="inline-flex items-center gap-1.5 px-4 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50">
                      <Pencil className="w-4 h-4" /> View Questions
                    </Link>
                  )}
                  {quiz.copyable && (
                    <button type="button" onClick={copyQuiz} disabled={copying} className="inline-flex items-center gap-1.5 px-4 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 disabled:opacity-50">
                      <Copy className="w-4 h-4" /> {copying ? 'Copying…' : 'Make a Copy for This Session'}
                    </button>
                  )}
                </div>
              )}
            </div>
            {me.canOpenQuizEditor && quiz.editBlock && (
              <p className="text-xs text-slate-500">
                {EDIT_BLOCK_TEXT[quiz.editBlock]}
              </p>
            )}
            {sitting && !sitting.inAudience && !isTrainer && (
              <p className="text-xs text-slate-500">The quiz is for people on the attendee list.</p>
            )}
            {sitting?.inAudience && me.role !== 'attended' && !sitting.done && (
              <p className="text-xs text-slate-500">
                You can sit the quiz even though you did not attend — passing it earns this session’s hours. Remember to file your absence as well.
              </p>
            )}
            {sitting?.inAudience && sitting.attempts > 0 && !sitting.done && (
              <p className="text-xs text-amber-700">
                Not passed after {sitting.attempts} attempt{sitting.attempts === 1 ? '' : 's'} — you can sit it again.
              </p>
            )}
          </div>
        ) : (
          <div className="px-5 py-6 text-center">
            {/* L&D decides whether there is a quiz (Sessions form): 'trainer'
                = write it here; 'none' = there is none, and the trainer
                cannot add one. */}
            <p className="text-sm text-slate-500">
              {session.quizMode === 'trainer'
                ? (trainerTools
                  ? 'No quiz yet. Write one and your participants will sit it after the session.'
                  : (isTrainer ? 'No quiz was written for this session.' : 'The trainers have not written the quiz yet.'))
                : (isTrainer
                  ? 'L&D set this session up without a quiz. Ask L&D to change it to “Trainer will write the quiz” if one is needed.'
                  : 'This session has no post-course quiz.')}
            </p>
            {trainerTools && me.canEditQuiz && session.quizMode === 'trainer' && (
              <Link to={`/my/sessions/${session.id}/quiz`} className={`${btnPrimary} inline-flex items-center gap-1.5 mt-3`}>
                <Plus className="w-4 h-4" /> Create Quiz
              </Link>
            )}
          </div>
        )}
      </Card>

      {/* Post-course feedback — only for someone who attended. */}
      {feedback && (
        <Card title="Post-Course Feedback" right={feedbackCta}>
          <div className="px-5 py-4">
            {feedback.submitted ? (
              <p className="text-sm text-slate-600">Thank you — your feedback for this session is on record.</p>
            ) : (
              <p className={`text-sm ${feedback.open ? 'text-amber-700' : 'text-slate-500'}`}>
                {feedback.open
                  ? 'Feedback not submitted yet — please rate this session.'
                  : 'The feedback form opens once the session has ended.'}
              </p>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default MySessionDetail;
